/**
 * Horizontal swipe → one step back or forward, for a phone.
 *
 * The calendar on a phone steps a day (Day view) or a week (Week view) when a
 * finger swipes across it — exactly what its ‹ › arrows do (Mario,
 * 2026-10-24: "on the calendar page, I want to be able to swipe to the left
 * and right and then go a day back and forward"). Finger moving LEFT pulls
 * the next period in from the right, so it steps FORWARD; moving right steps
 * back, as every phone calendar does.
 *
 * The page also scrolls vertically under the same finger, and a selected slot
 * is dragged by it, so a gesture is classified before it does anything:
 *
 * 1. `swipeAxis` — nothing happens until the finger has travelled
 *    `SWIPE_LOCK_PX`; then the gesture is horizontal only if its sideways
 *    travel beats its vertical travel by `SWIPE_AXIS_RATIO`. Anything else is
 *    a scroll and is left to the browser for the rest of the gesture.
 * 2. `swipeStep` — on release, a horizontal gesture steps if it travelled far
 *    enough (`SWIPE_COMMIT_PX`, or `SWIPE_COMMIT_FRACTION` of the width on a
 *    wide surface), or if it was a quick flick. Otherwise it springs back.
 *
 * Pure, so the thresholds have one definition and one test; the DOM half is
 * `use-swipe-step.ts`.
 */

/** Travel before a gesture commits to an axis — under it, it is still a tap. */
export const SWIPE_LOCK_PX = 10;
/** Sideways travel must exceed vertical travel by this factor to be a swipe. */
export const SWIPE_AXIS_RATIO = 1.2;
/** Travel that always steps, however slow the finger. */
export const SWIPE_COMMIT_PX = 60;
/** …or this share of the surface's width, whichever is larger. */
export const SWIPE_COMMIT_FRACTION = 0.18;
/** A flick: at least this fast (px per ms) over at least `SWIPE_FLICK_MIN_PX`. */
export const SWIPE_FLICK_SPEED = 0.45;
export const SWIPE_FLICK_MIN_PX = 30;

export type SwipeAxis = "x" | "y";

/**
 * The axis a gesture has committed to, or null while it is still inside the
 * lock radius. Decided once per gesture by the caller: re-asking later would
 * let a scroll that drifts sideways turn into a swipe halfway down the day.
 */
export function swipeAxis(dx: number, dy: number): SwipeAxis | null {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (Math.max(ax, ay) < SWIPE_LOCK_PX) return null;
  return ax > ay * SWIPE_AXIS_RATIO ? "x" : "y";
}

/**
 * The step a released horizontal gesture asks for: +1 forward (finger moved
 * left), -1 back (finger moved right), 0 to spring back. `dtMs` is the
 * gesture's duration and `width` the swiped surface's width in px.
 */
export function swipeStep(dx: number, dtMs: number, width: number): -1 | 0 | 1 {
  const travel = Math.abs(dx);
  const far = travel >= Math.max(SWIPE_COMMIT_PX, width * SWIPE_COMMIT_FRACTION);
  const flick =
    travel >= SWIPE_FLICK_MIN_PX && dtMs > 0 && travel / dtMs >= SWIPE_FLICK_SPEED;
  if (!far && !flick) return 0;
  return dx < 0 ? 1 : -1;
}
